// Utility functions for generating whiteboard JSON.

import {
  type DiagramNode,
  type DiagramEdge,
} from '../parsing/DiagramStructure';
import type Diagram from './Diagram';
import { type ColorInputs } from '../colors/ColorScheme';
import { getConnectorEndStyleNumber } from './renderingUtils';

// Return a whiteboard end style from a string propery
const whiteboardConnectorEndStyle = (style: string): number => {
  return getConnectorEndStyleNumber(style);
};

// Generate a whiteboard object from a diagram node.
const whiteboardObjectForNode = (
  whiteboard: any,
  diagram: Diagram,
  node: DiagramNode,
  colorInputs: ColorInputs,
  overrides?: Partial<fabric.Object>
): any => {
  if (node.database && node.subDiagram) {
    const subDiagram = node.subDiagram;
    const subWhiteboard = subDiagram.renderToWhiteboard();
    // Add sub-diagram objects to the whiteboard.
    for (const id in subWhiteboard.objects) {
      const subObject = subWhiteboard.objects[id];
      if (subObject.hasOwnProperty('left') && subObject.hasOwnProperty('top')) {
        // We need to adjust the position of the sub-diagram objects.
        subObject.left += node.layout.left;
        subObject.top += node.layout.top;
      }
      whiteboard.objects[id] = subObject;
    }

    // Render a border around the sub-diagram. This was primarily for debugging, it should be optional.
    return (whiteboard.objects[node.id] = {
      type: 'shape',
      shapeName: 'rect',
      id: node.id,
      left: node.layout.left,
      top: node.layout.top,
      width: subDiagram.width(),
      height: subDiagram.height(),
      scaleX: 1,
      scaleY: 1,
      fill: 'transparent',
      stroke: '#cccccc',
      strokeWidth: 1,
      ...overrides,
    });
  }

  const shapeName = node.layout.shape || diagram.getNodeProperty(node, 'shape');
  const colorScheme = diagram.getColorScheme();
  const objectColor = {
    ...colorScheme.objectColor({ shape: shapeName, ...colorInputs }),
  };

  if (shapeName === 'text') {
    objectColor.fill = 'transparent';
    objectColor.stroke = 'transparent';
  }

  return (whiteboard.objects[node.id] = {
    type: 'shape',
    shapeName: shapeName,
    id: node.id,
    left: node.layout.left,
    top: node.layout.top,
    width: node.layout.width,
    height: node.layout.height,
    scaleX: 1,
    scaleY: 1,
    textContent: `<p><span style="color:${objectColor.text}">${
      node.title || node.id
    }</span></p>`,
    fill: objectColor.fill,
    stroke: objectColor.stroke,
    strokeWidth: objectColor.strokeWidth,
    ...overrides,
  });
};

const edgeToAttachmentPoint = (
  diagram: Diagram,
  edge: DiagramEdge,
  start: boolean
) => {
  // TODO: For this to work effectively we need to be able to convert a point
  // on the perimeter of the node to a ratio of the perimeter, and then use
  // that as the attachment point. Otherwise we end up with two problems:
  //
  //  1. We cannot position connectors that dagre wants to slightly offset.
  //  2. Connectors do not attach to the correct side.
  //
  // e.g. try these diagrams:
  /*
flowchart

Start -> Finish
Int -> Finish
Finish -> Start
Finish -> Int

----
flowchart

Start -> Finish
Start -> Int
Int -> Finish

*/

  if (!edge.layout.points || edge.layout.points === 0) {
    return 'cc';
  }

  let nodeId;
  let point;
  if (start) {
    nodeId = edge.from;
    point = edge.layout.points[0];
  } else {
    nodeId = edge.to;
    point = edge.layout.points[edge.layout.points.length - 1];
  }
  const node = diagram.database.node(nodeId);
  const halfWidth = node.layout.left + node.layout.width / 2;
  const halfHeight = node.layout.top + node.layout.height / 2;
  if (point.x <= node.layout.left) {
    if (point.y < halfHeight) {
      return 'ct';
    } else if (point.y > halfHeight) {
      return 'cb';
    } else {
      return 'cl';
    }
  } else if (point.x >= node.layout.left + node.layout.width) {
    if (point.y < halfHeight) {
      return 'ct';
    } else if (point.y > halfHeight) {
      return 'cb';
    } else {
      return 'cr';
    }
  } else {
    // Top or bottom
    if (point.y <= node.layout.top) {
      if (point.x < halfWidth) {
        return 'cl';
      } else if (point.x > halfWidth) {
        return 'cr';
      } else {
        return 'ct';
      }
    } else {
      if (point.x < halfWidth) {
        return 'cl';
      } else if (point.x > halfWidth) {
        return 'cr';
      } else {
        return 'cb';
      }
    }
  }
};

// Generate a whiteboard object from a diagram node.
const whiteboardConnectorForEdge = (
  diagram: Diagram,
  edge: DiagramEdge,
  overrides?: Partial<fabric.Object>
): any => {
  const colorScheme = diagram.getColorScheme();
  const connectorColor = { ...colorScheme.connectorColor({}) };

  return {
    type: 'connector',
    id: edge.id,
    lineRouting: 20,
    stroke: connectorColor.stroke,
    strokeWidth: connectorColor.strokeWidth,
    attachments: [
      {
        objectId: edge.from,
        attachmentPoint: edgeToAttachmentPoint(diagram, edge, true), // 'cb',
      },
      {
        objectId: edge.to,
        attachmentPoint: edgeToAttachmentPoint(diagram, edge, false), // 'ct',
      },
    ],
    endStyles: [
      whiteboardConnectorEndStyle(
        diagram.getEdgeProperty(edge, 'connectorFromStyle') as string
      ),
      whiteboardConnectorEndStyle(
        diagram.getEdgeProperty(edge, 'connectorToStyle') as string
      ),
    ],
    points: [
      // These are fake points. They will be regenerated when the whiteboard loads.
      { x: 0, y: 0 },
      { x: 100, y: 100 },
    ],
    ...overrides,
  };
};

export {
  whiteboardConnectorEndStyle,
  whiteboardObjectForNode,
  whiteboardConnectorForEdge,
};
